import React, { useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArtworkUpload } from "../components/ArtworkUpload";
import { CheckList, Row } from "./PullUpBuilder";
import { FIXED_PRODUCTS } from "../data/fixedProducts";
import { formatAUDexact } from "../data/signsPricing";
import { Image as ImageIcon } from "lucide-react";

function initialSelection(info, preset) {
  const sel = {};
  info.steps.forEach((st) => {
    if (st.input) { sel[st.key] = "input"; return; }
    const opts = st.options(sel);
    sel[st.key] = opts.some((o) => String(o.id) === preset[st.key]) ? opts.find((o) => String(o.id) === preset[st.key]).id : opts[0].id;
  });
  return sel;
}

export default function FixedBuilder({ product }) {
  const info = FIXED_PRODUCTS[product];
  const [params] = useSearchParams();
  const [sel, setSel] = useState(() => initialSelection(info, Object.fromEntries(params)));
  const [qtyText, setQtyText] = useState(sel.qty === "input" ? "1" : "");
  const [file, setFile] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const optionsFor = (st) => (st.input ? [] : st.options(sel));
  const chosen = useMemo(() => Object.fromEntries(info.steps.map((st) => [st.key, optionsFor(st).find((o) => o.id === sel[st.key])])), [info, sel]); // eslint-disable-line react-hooks/exhaustive-deps
  const typed = parseInt(qtyText, 10) || 0;
  const quote = sel.qty === "custom";
  const effSel = sel.qty === "input" || quote ? { ...sel, qty: typed } : sel;
  const subtotal = quote ? null : info.price(effSel);
  const gst = subtotal == null ? null : subtotal * 0.1;
  const qty = effSel.qty || 0;
  const [one, many] = info.unitWord;

  const addToCart = () => {
    if ((quote || sel.qty === "input") && typed < 1) { toast.error(quote ? "Please enter your custom quantity" : "Please enter a quantity"); return; }
    if (!quote && subtotal == null) { toast.error("Please choose a valid option"); return; }
    if (!file) { toast.error("Please upload your artwork first"); return; }
    const fields = info.cart(effSel, chosen);
    addItem({
      product_type: info.id,
      unit_price: quote ? 0 : subtotal / fields.quantity,
      line_total: quote ? 0 : subtotal,
      needs_quote: quote,
      artwork_file_id: file.id,
      artwork_filename: file.name,
      ...fields,
    });
    toast.success(quote ? "Added — we'll send you a quote" : `${fields.product_name} added to cart`);
    navigate("/cart");
  };

  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6">
        <div className="eyebrow" data-testid="sign-eyebrow">{info.shortName} · Custom print</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight" data-testid="fixed-product-title">{info.headline}</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl">{info.tagline}</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-6 space-y-6">
          <ProductPhoto info={info} />
          <CheckList title="Ideal for" items={info.uses} grid />
          <CheckList title="About this product" items={info.features} />
        </div>

        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card p-6 space-y-6">
            {info.steps.map((st, idx) => (
              <OptionStep key={st.key} step={st} index={idx + 1} options={optionsFor(st)} value={sel[st.key]} onChange={(v) => setSel((s) => ({ ...s, [st.key]: v }))}
                qtyText={qtyText} setQtyText={setQtyText} />
            ))}
            {quote && <p className="text-xs text-slate-500" data-testid="fixed-custom-qty-note">Custom quantities are priced on request — add it to your cart and we'll send you a quote.</p>}
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow mb-3">Step {info.steps.length + 1} — Upload artwork</div>
            <ArtworkUpload file={file} setFile={setFile} />
            <p className="text-xs text-slate-500 mt-3" data-testid="fixed-artwork-guidance">{info.artworkNote}</p>
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow">Total order price</div>
            {quote ? (
              <div className="mt-2" data-testid="sign-quote">
                <div className="font-display text-4xl font-black">Custom quote</div>
                <div className="text-slate-600 mt-1 text-sm">{typed > 0 ? `${typed} ${typed === 1 ? one : many} — we'll confirm your price by email.` : "Enter your quantity above."}</div>
              </div>
            ) : (<>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-5xl font-black price-tick" data-testid="sign-total-exgst">{subtotal == null ? "—" : formatAUDexact(subtotal)}</div>
              <div className="text-sm text-slate-500 font-semibold">ex GST</div>
            </div>
            <div className="text-slate-600 mt-1 text-sm" data-testid="sign-unit-price">
              {qty < 1 ? "Enter your quantity above." : <>Total for {qty} {qty === 1 ? one : many}{qty > 1 && subtotal != null ? ` · works out at ${formatAUDexact(subtotal / qty)} each` : ""}</>}
            </div>
            </>)}
            <div className="mt-5 space-y-2 text-sm">
              <Row label="Product" value={sel.type ? info.cart(effSel, chosen).product_name : info.name} testid="sign-summary-product"/>
              {info.steps.filter((st) => st.key !== "type" && st.key !== "qty").map((st) => (
                <Row key={st.key} label={st.label} value={chosen[st.key]?.label} testid={`sign-summary-${st.key}`}/>
              ))}
              <Row label="Quantity" value={effSel.qty ? String(effSel.qty) : "—"} testid="sign-summary-qty"/>
              {info.cart(effSel, chosen).print_sides && <Row label="Printing" value="Full-colour, double-sided" testid="sign-summary-sides"/>}
              <Row label="Artwork" value={file ? file.name : "Not uploaded"} testid="sign-summary-artwork"/>
            </div>
            {!quote && <>
            <div className="h-px bg-[#E2DDD5] my-4"/>
            <div className="space-y-1 text-sm">
              <Row label="Subtotal (ex GST)" value={formatAUDexact(subtotal)} testid="sign-row-subtotal"/>
              <Row label="GST (10%)" value={formatAUDexact(gst)} testid="sign-row-gst"/>
              <div className="flex justify-between items-baseline pt-2">
                <div className="font-display font-bold">Total inc GST</div>
                <div className="font-display font-black text-2xl price-tick" data-testid="sign-total-incgst">{subtotal == null ? "—" : formatAUDexact(subtotal + gst)}</div>
              </div>
            </div>
            </>}
            <button onClick={addToCart} className="btn-primary w-full mt-5" data-testid="sign-add-to-cart">{quote ? "Request quote" : "Add to Cart"}</button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function ProductPhoto({ info }) {
  return (
    <div className="sf-card overflow-hidden">
      <div className="aspect-[5/4] relative bg-[#F6F8FD]">
        {info.image ? (
          <img src={info.image} alt={info.imageAlt} className="w-full h-full object-cover" data-testid="sign-product-photo" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-[#003DA5]/50" data-testid="sign-product-photo-placeholder">
            <ImageIcon size={48} />
            <div className="font-display font-bold text-slate-500">{info.name}</div>
            <div className="text-xs text-slate-400">Product photo coming soon</div>
          </div>
        )}
      </div>
    </div>
  );
}

function QtyInput({ value, onChange }) {
  return (
    <input type="text" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="Enter quantity"
      className="h-11 w-40 rounded-full border-2 border-[#E2DDD5] bg-white px-4 font-semibold focus:outline-none focus:border-[color:var(--brand)]" data-testid="fixed-qty-input" />
  );
}

function OptionStep({ step, index, options, value, onChange, qtyText, setQtyText }) {
  return (
    <div>
      <div className="eyebrow mb-3">Step {index} — {step.label}</div>
      {step.input ? (
        <QtyInput value={qtyText} onChange={setQtyText} />
      ) : step.cards ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {options.map((o) => (
            <button key={o.id} onClick={() => onChange(o.id)} aria-pressed={value === o.id}
              className={`rounded-2xl border-2 p-4 text-left transition-colors ${value === o.id ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-[#E2DDD5] bg-white hover:border-[color:var(--brand)]"}`}
              data-testid={`fixed-${step.key}-${o.id}`}>
              <div className="font-display font-bold">{o.label}</div>
              {o.note && <div className="text-xs text-slate-500 mt-1">{o.note}</div>}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 items-center">
          {options.map((o) => (
            <button key={o.id} onClick={() => onChange(o.id)} className={`chip ${value === o.id ? "chip-active" : ""}`} data-testid={`fixed-${step.key}-${o.id}`}>{o.label}</button>
          ))}
          {value === "custom" && <QtyInput value={qtyText} onChange={setQtyText} />}
        </div>
      )}
    </div>
  );
}
